// =====================================================================
// src/screens/v2/BusinessWallPostDetail.jsx
//
// Phase 2.5 / C10c — Customer-facing single wall post view.
//
// Reached three ways:
//   1. Tap a post card on BusinessProfile's Wall sub-tab
//   2. Deep link /biz/CODE/post/POSTID → V1 URL parser → V2App routes here
//   3. Tap a post link inside an Aangan chat message
//
// Renders:
//   - Top bar with back arrow + biz logo/name (tap to open biz profile)
//   - The single post (WallPostCard) with full engagement row
//   - Comments thread (real-time subscription via listComments)
//   - Comment composer at the bottom
//
// Handles the "post removed" case: if the underlying post doc is deleted
// while the user is viewing, the snapshot listener returns null and we
// switch to a clean "Post no longer available" screen with a Back-to-
// business CTA.
//
// Sparks + comments wire up here (not C10d-deferred) because the helpers
// already exist in wallPosts.js. C10d's scope shrinks to per-user spark
// state polish (showing whether the current user has sparked).
// =====================================================================

import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Send, Trash2, AlertTriangle } from 'lucide-react';
import {
  doc as fsDoc,
  getDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import {
  toggleSpark,
  addComment,
  listComments,
  deleteComment,
} from '../../lib/v2/wallPosts';
import WallPostCard from './WallPostCard';
import { submitBusinessReport } from '../../lib/db';
import SharePostSheet from './SharePostSheet';
import ImageViewerModal from '../../components/ImageViewerModal';

export default function BusinessWallPostDetail({
  bizCode,
  postId,
  currentUser,
  onBack,
  onOpenBiz,
  onSetToast,
}) {
  // ---- Post + business state ----
  // `post` is null while loading, false if confirmed deleted, the post
  // object otherwise. This three-state pattern lets us distinguish
  // "still loading" from "definitively gone" so the empty state only
  // appears when we KNOW the post doesn't exist.
  const [post, setPost] = useState(null);
  const [postDeleted, setPostDeleted] = useState(false);
  const [business, setBusiness] = useState(null);

  // Comments
  const [comments, setComments] = useState([]);
  const [commentDraft, setCommentDraft] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const commentInputRef = useRef(null);

  // Per-user spark state — loaded once on mount, then mutated optimistically
  // by handleSpark. Real source of truth is the sparks/{userId} doc; we
  // re-read it on toggle so concurrent sparks from another device sync up.
  const [sparkedByMe, setSparkedByMe] = useState(false);

  // Sheets / modals
  const [shareOpen, setShareOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null);

  // ---- Subscribe to the post doc (real-time) ----
  // We use onSnapshot rather than a one-shot getDoc so the screen
  // reflects edits made by the merchant + flips to the "removed"
  // state if the post is deleted while the user is reading.
  useEffect(() => {
    if (!bizCode || !postId) return;
    setPostDeleted(false);
    setPost(null);
    const unsub = onSnapshot(
      fsDoc(db, 'businesses', bizCode, 'posts', postId),
      (snap) => {
        if (!snap.exists()) {
          setPost(false);
          setPostDeleted(true);
          return;
        }
        setPost({ id: snap.id, ...snap.data() });
      },
      (err) => {
        console.error('Post snapshot error:', err);
        // Permission-denied on a signed-in user usually means the post
        // doesn't exist (rule edge cases). Treat as deleted.
        if (err?.code === 'permission-denied') {
          setPost(false);
          setPostDeleted(true);
        } else {
          onSetToast?.(`Couldn't load post: ${err?.code || err?.message || 'try again'}`);
        }
      }
    );
    return unsub;
  }, [bizCode, postId, onSetToast]);

  // ---- Load business info once for the header ----
  useEffect(() => {
    if (!bizCode) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(fsDoc(db, 'businesses', bizCode));
        if (!cancelled && snap.exists()) {
          setBusiness({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.warn('Could not load business for post detail:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [bizCode]);

  // ---- Subscribe to comments (real-time, oldest first) ----
  useEffect(() => {
    if (!bizCode || !postId) return;
    return listComments({
      bizId: bizCode,
      postId,
      onSnap: setComments,
      onError: (err) => onSetToast?.(`Couldn't load comments: ${err.code || err.message}`),
      limit: 100,
    });
  }, [bizCode, postId, onSetToast]);

  // ---- Per-user spark — one-time read on mount ----
  useEffect(() => {
    if (!bizCode || !postId || !currentUser?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(
          fsDoc(db, 'businesses', bizCode, 'posts', postId, 'sparks', currentUser.id)
        );
        if (!cancelled) setSparkedByMe(snap.exists());
      } catch (err) {
        // Non-fatal — defaults to not-sparked. User can still tap to spark.
        console.warn('Spark state read failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [bizCode, postId, currentUser?.id]);

  // ---- Handlers ----

  async function handleSpark() {
    if (!currentUser?.id) {
      onSetToast?.('Sign in to spark this post');
      return;
    }
    // Optimistic UI: flip immediately, then reconcile from the
    // transaction result.
    const wasSparked = sparkedByMe;
    setSparkedByMe(!wasSparked);
    try {
      const result = await toggleSpark({
        bizId: bizCode,
        postId,
        userId: currentUser.id,
      });
      setSparkedByMe(result.sparked);
    } catch (err) {
      // Revert optimistic update.
      setSparkedByMe(wasSparked);
      console.error('toggleSpark failed:', err);
      onSetToast?.(`Couldn't update spark: ${err.code || err.message || 'try again'}`);
    }
  }

  async function handleSubmitComment(e) {
    e?.preventDefault?.();
    const text = commentDraft.trim();
    if (!text || submittingComment) return;
    setSubmittingComment(true);
    try {
      await addComment({
        bizId: bizCode,
        postId,
        authorId: currentUser.id,
        text,
      });
      setCommentDraft('');
    } catch (err) {
      console.error('addComment failed:', err);
      onSetToast?.(`Couldn't post comment: ${err.code || err.message || 'try again'}`);
    } finally {
      setSubmittingComment(false);
    }
  }

  async function handleDeleteComment(comment) {
    if (!comment?.id) return;
    const ok = typeof window !== 'undefined'
      ? window.confirm('Delete this comment?')
      : true;
    if (!ok) return;
    try {
      await deleteComment({ bizId: bizCode, postId, commentId: comment.id });
    } catch (err) {
      console.error('deleteComment failed:', err);
      onSetToast?.(`Couldn't delete: ${err.code || err.message || 'try again'}`);
    }
  }

  // ---- Render: deleted/not-found case ----
  if (postDeleted) {
    return (
      <div className="v2-biz-profile flex flex-col" style={{ background: 'var(--bg)' }}>
        <div className="flex items-center px-3 py-3 shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
          <button onClick={onBack} className="w-9 h-9 rounded-full flex items-center justify-center active:scale-95"
                  aria-label="Back">
            <ArrowLeft size={20} style={{ color: 'var(--ink)' }} />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
               style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
            <AlertTriangle size={26} style={{ color: 'var(--accent)' }} strokeWidth={1.5} />
          </div>
          <h2 className="mb-2"
              style={{ fontSize: '20px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
            Post no longer available
          </h2>
          <p className="text-[13px] mb-5 max-w-[280px]" style={{ color: 'var(--ink-soft)' }}>
            This post may have been removed or is no longer shared. You can still visit the business profile.
          </p>
          {business && (
            <button onClick={() => onOpenBiz?.(bizCode)}
              className="h-11 px-5 rounded-full font-medium text-[14px] active:scale-[0.98] transition-transform"
              style={{ background: 'var(--ink)', color: '#fff' }}>
              Go to {business.name || 'business'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ---- Render: loading ----
  if (post === null) {
    return (
      <div className="v2-biz-profile flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="w-10 h-10 rounded-full border-2 animate-spin"
             style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
      </div>
    );
  }

  // ---- Render: post present ----
  return (
    <div className="v2-biz-profile flex flex-col overflow-hidden" style={{ background: 'var(--bg)' }}>
      {/* Top bar */}
      <div className="flex items-center gap-3 px-3 py-3 shrink-0"
           style={{ borderBottom: '1px solid var(--border)' }}>
        <button onClick={onBack} className="w-9 h-9 rounded-full flex items-center justify-center active:scale-95"
                aria-label="Back">
          <ArrowLeft size={20} style={{ color: 'var(--ink)' }} />
        </button>
        <button onClick={() => onOpenBiz?.(bizCode)}
                className="flex items-center gap-2 min-w-0 flex-1 active:opacity-80"
                aria-label="View business profile">
          {business?.logoImageUrl ? (
            <img src={business.logoImageUrl} alt=""
                 style={{ width: 32, height: 32, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <div style={{ width: 32, height: 32, borderRadius: 10, background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 16 }}>{business?.name?.[0] || '🏪'}</span>
            </div>
          )}
          <div className="flex-1 min-w-0 text-left">
            <div className="text-[14px] font-medium truncate" style={{ color: 'var(--ink)' }}>
              {business?.name || '…'}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
              View business profile
            </div>
          </div>
        </button>
      </div>

      {/* Scrollable: post + comments */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4">
        <WallPostCard
          post={post}
          variant="customer"
          sparkedByMe={sparkedByMe}
          onSpark={handleSpark}
          onComment={() => commentInputRef.current?.focus()}
          onShare={() => setShareOpen(true)}
          onTapPhoto={(url) => setViewerUrl(url)}
          // Session C1 — report this post. Shown only when signed in and the
          // viewer is not the business owner. Writes a business_post report.
          onReport={(currentUser?.id && currentUser.id !== business?.ownerId) ? (async ({ reason, detail }) => {
            try {
              const res = await submitBusinessReport({
                businessId: bizCode,
                targetId: postId,
                reporterId: currentUser.id,
                reason,
                detail,
              });
              onSetToast?.(res.duplicate
                ? 'Already reported — we\'re reviewing.'
                : 'Reported. We\'ll review within 24 hours.');
            } catch (err) {
              console.error('submitBusinessReport failed:', err);
              onSetToast?.('Could not submit report — try again');
            }
          }) : null}
        />

        {/* Comments section — matches V1 PostDetail style */}
        <div className="mt-3 mb-1">
          <div className="text-[11px] uppercase tracking-wider px-1"
               style={{ color: 'var(--ink-soft)' }}>
            Comments{comments.length > 0 ? ` · ${comments.length}` : ''}
          </div>
        </div>
        {comments.length === 0 ? (
          <p className="text-[13px] italic py-4 px-1" style={{ color: 'var(--ink-soft)' }}>
            Be the first to comment.
          </p>
        ) : (
          comments.map((c) => (
            <CommentRow
              key={c.id}
              comment={c}
              currentUser={currentUser}
              businessOwnerId={business?.ownerId}
              onDelete={() => handleDeleteComment(c)}
            />
          ))
        )}
      </div>

      {/* Comment composer pinned at bottom — matches V1 PostDetail
          composer layout (avatar + input + send button). Enter submits;
          Shift+Enter inserts a newline. Disabled state surfaces a clear
          "Sign in to comment" placeholder. */}
      <form onSubmit={handleSubmitComment}
            className="shrink-0 flex items-center gap-2 px-3 py-2.5"
            style={{ borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
        {/* User avatar */}
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
             style={{ background: currentUser?.color || 'var(--accent)', color: '#fff' }}>
          {currentUser?.photoUrl ? (
            <img src={currentUser.photoUrl} alt=""
                 style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span className="text-[12px] font-medium">
              {(currentUser?.name?.[0] || '?').toUpperCase()}
            </span>
          )}
        </div>
        <textarea
          ref={commentInputRef}
          value={commentDraft}
          onChange={(e) => setCommentDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter submits, Shift+Enter inserts newline (V1 parity).
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmitComment(e);
            }
          }}
          placeholder={currentUser?.id ? 'Add a comment…' : 'Sign in to comment'}
          disabled={!currentUser?.id || submittingComment}
          rows={1}
          className="flex-1 px-4 py-2 rounded-full text-[14px] leading-snug resize-none"
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            color: 'var(--ink)',
            outline: 'none',
            maxHeight: 120,
          }}
        />
        <button
          type="submit"
          disabled={!commentDraft.trim() || submittingComment || !currentUser?.id}
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 active:scale-95"
          style={{
            background: commentDraft.trim() && !submittingComment ? 'var(--ink)' : 'var(--border)',
            color: '#fff',
            opacity: submittingComment ? 0.6 : 1,
          }}
          aria-label="Send comment">
          <Send size={16} />
        </button>
      </form>

      {/* Share sheet */}
      {shareOpen && (
        <SharePostSheet
          post={post}
          business={business}
          currentUser={currentUser}
          onClose={() => setShareOpen(false)}
          onSetToast={onSetToast}
        />
      )}

      {viewerUrl && (
        <ImageViewerModal src={viewerUrl} onClose={() => setViewerUrl(null)} />
      )}
    </div>
  );
}

// =====================================================================
// CommentRow — single comment with author name + delete affordance.
//
// Author info is hydrated from /users/{authorId} on render. We don't
// pre-fetch all author docs to keep the page light; per-row reads are
// cached across renders by Firestore.
//
// Delete affordance shows for: comment author OR the business owner.
// Anyone else sees no delete button (rule + UI agree). Platform admins
// can delete via the rule but we don't surface UI for that here.
// =====================================================================

function CommentRow({ comment, currentUser, businessOwnerId, onDelete }) {
  const [author, setAuthor] = useState(null);
  useEffect(() => {
    if (!comment?.authorId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(fsDoc(db, 'users', comment.authorId));
        if (!cancelled && snap.exists()) {
          setAuthor({ id: snap.id, ...snap.data() });
        }
      } catch (err) { /* unknown author — render fallback */ }
    })();
    return () => { cancelled = true; };
  }, [comment?.authorId]);

  const canDelete =
    currentUser?.id === comment?.authorId
    || currentUser?.id === businessOwnerId;

  const ts = comment?.createdAt?.toDate ? comment.createdAt.toDate() : null;
  const ageString = ts ? relativeAge(ts) : 'just now';

  return (
    <div className="flex items-start gap-2.5 py-2.5 px-1"
         style={{ borderTop: '1px solid var(--border)' }}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-medium shrink-0"
           style={{ background: author?.color || 'var(--accent)', color: '#fff' }}>
        {author?.photoUrl ? (
          <img src={author.photoUrl} alt=""
               style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          (author?.name?.[0] || '?').toUpperCase()
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>
            {author?.name || 'Member'}
          </span>
          <span className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
            {ageString}{comment.editedAt ? ' · edited' : ''}
          </span>
        </div>
        <p className="text-[13px] leading-snug mt-0.5 whitespace-pre-wrap"
           style={{ color: 'var(--ink)' }}>
          {comment.text}
        </p>
      </div>
      {canDelete && (
        <button onClick={onDelete}
                className="w-7 h-7 rounded-full flex items-center justify-center active:scale-95 shrink-0"
                aria-label="Delete comment">
          <Trash2 size={13} style={{ color: 'var(--ink-soft)' }} />
        </button>
      )}
    </div>
  );
}

function relativeAge(date) {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  return date.toLocaleDateString();
}
